import { GatewayControleQuinquennal } from "@conformite/gateway";
import { isAxiosError } from "axios";

/**
 * Formatage du bloc d'estimation coût/durée d'un lancement CQ
 * (conception EN-1476 §5). Fonctions pures : aucune
 * valeur de repli, aucun chiffre inventé — l'appelant ne les invoque que sur
 * une valeur déjà non nulle renvoyée par la gateway.
 */

/**
 * "≈ 4 min" sous l'heure, "≈ 2 h 10" au-delà (minutes à deux chiffres,
 * omises si nulles : "≈ 2 h").
 */
export function formatEstimatedDuration(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) {
    return `≈ ${totalMinutes} min`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0
    ? `≈ ${hours} h`
    : `≈ ${hours} h ${String(minutes).padStart(2, "0")}`;
}

/**
 * "≈ 0,42 USD" — virgule décimale française, devise explicite en toutes
 * lettres. Jamais `€` : le calcul gateway est en dollars, un symbole ferait
 * croire à un montant en euros.
 *
 * Un montant non nul mais inférieur au centime s'affiche "< 0,01 USD" et non
 * "≈ 0,00 USD" : arrondi à zéro, le bloc affirmerait que le lancement est
 * gratuit. C'est le seul endroit du dispositif où l'affichage pourrait
 * rassurer à tort, alors que dissuader le clic accidentel en est l'objet.
 */
export function formatEstimatedCost(usd: number): string {
  const formatter = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (usd > 0 && usd < 0.005) {
    return `< ${formatter.format(0.01)} USD`;
  }
  return `≈ ${formatter.format(usd)} USD`;
}

export const ANALYSE_COST_BLOCKED_MESSAGE =
  "Analyse IA bloquée : son coût estimé dépasse le plafond autorisé.";

/**
 * Message à afficher à la place de la modale de lancement quand le coût
 * estimé dépasse le plafond renvoyé par la gateway, `null` sinon (coût sous
 * le plafond ou non estimable : la gateway laisse alors passer).
 */
export function getAnalyseCostBlockedMessage(
  estimation?: GatewayControleQuinquennal.ControleQuinquennalAnalyseEstimationResponse
): string | null {
  const cost = estimation?.estimatedCostUsd;
  if (!estimation || cost == null || cost <= estimation.costThresholdUsd) {
    return null;
  }
  const threshold = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2,
  }).format(estimation.costThresholdUsd);
  return `Analyse IA bloquée : coût estimé ${formatEstimatedCost(cost)}, au-delà du plafond de ${threshold} USD.`;
}

export function isAnalyseCostBlockedError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 422;
}
